import type { AxiosError } from "axios";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { AccordionCardWithEditFeatures } from "../AccordionCard/AccordionCardwithEditFeatures";
import { AttributeGroupFormGenerator } from "../AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { Notifications } from "../Notifications/NotificationsContext";
import type { DataMutate } from "../../types/types";
import type {
  AttributeGroupSummary,
  AttributeSchema,
  DocumentAttributeValue,
  ImageAttributeValue,
  PIMProduct,
} from "../../types/types.PIM";
import { ProductGroupFormValues } from "../../pages/SharedPages/ProductDetailPage/ProductOverview/product_overview.utils";
import { ObjectReferenceChangeConfirmDialog } from "../ObjectReference/ObjectReferenceChangeConfirmDialog";
import {
  shouldConfirmObjectReferenceChange,
  shouldShowObjectReferenceAttribute,
} from "../ObjectReference/utils";
import { useStoreState } from "../../util/util";
import { buildAttributesPatchData } from "../../pages/SharedPages/ProductDetailPage/ProductOverview/attributePatch.utils";
import { isDocumentAttribute } from "../AttributeGroupFormGenerator/DocumentAttribute/documentAsset.utils";
import {
  isAttributeEditRejected,
  getAttributeEditRejectedMessage,
} from "../../util/attributeEditability";
import type { AttributeGroupFormGeneratorProps } from "../AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { ChangeRequestFormModal } from "../ChangeRequestForm/ChangeRequestModal";
import {
  ChangeRequestFormValues,
  resolveAttributeOptions,
  submitChangeRequest,
} from "../ChangeRequestForm/changerequest.util";
import { ENTITY_MODIFY_PERMISSIONS, EntityType } from "../../types/types.MDM";
import { isAttributeVisibleInCard } from "../../pages/admin/SellerAdmin/MDM/shared/utils";
import { useAuthContext } from "../Auth";

const EditFormWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

export type AttributeGroupEditFormProps = {
  attributes: AttributeSchema[];
} & Omit<AttributeGroupFormGeneratorProps, "attributes">;

export function AttributeGroupEditForm({
  attributes,
  ...formProps
}: AttributeGroupEditFormProps): JSX.Element {
  return <AttributeGroupFormGenerator attributes={attributes} {...formProps} />;
}

const EDITABLE_ATTRIBUTE_DEBUG_FLAG = "mdm_attribute_debug";

const isEditableAttributeDebugEnabled = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);
  return (
    window.localStorage.getItem(EDITABLE_ATTRIBUTE_DEBUG_FLAG) === "1" ||
    searchParams.get(EDITABLE_ATTRIBUTE_DEBUG_FLAG) === "1"
  );
};

/**
 * Configuration for AttributeGroupCard behavior
 */
export type AttributeGroupCardConfig<T> = {
  /** Data object (product, entity, etc.) */
  data: T;
  /** Group to render */
  group: AttributeGroupSummary;
  /** SWR mutate function */
  mutate: DataMutate<T>;
  /** API endpoint for patching group values */
  groupEndpoint: string;
  /** Component to render attribute values in read mode */
  detailsComponent: React.ComponentType<{
    group: AttributeGroupSummary;
  }>;
  /** Callback after successful save (optional) */
  onSaveSuccess?: (updatedData: T) => void;
  /** Show mandatory asterisk on required fields */
  showMandatoryAsterisk?: boolean;
  /** Callback to create new list option (optional) */
  onCreateOption?: (value: string, attribute: AttributeSchema) => Promise<void>;
  /** Callback to create image attribute (optional) */
  onCreateImageAttribute?: (
    image_arr: ImageAttributeValue | null,
    attribute: AttributeSchema & { attribute_id?: string }
  ) => void;
  /** Callback to create document attribute (optional) */
  onCreateDocumentAttribute?: (
    docs: DocumentAttributeValue[],
    attribute: AttributeSchema & { attribute_id?: string }
  ) => void;
  /** Optional product context for document attribute asset forms */
  product?: PIMProduct;
  /** Callback to update applicability (optional) */
  updateApplicability?: (
    attributesToMakeApplicable: string[],
    attributesToMakeUnapplicable: string[]
  ) => Promise<void>;
  /** Whether to hide attributes where is_visible is false when editing */
  filterInvisibleAttributesInEditMode?: boolean;
  /** Whether the card is editable (controls edit button visibility). Defaults to true. */
  isEditable?: boolean;
  /** Called after a successful save when new document attribute assets are present. */
  onAssociateDocumentAssets?: (assetIds: string[]) => Promise<void>;
  /** Profile id to exclude from object-reference search when source and target types match. */
  objectSearchExcludeProfileId?: string;
  isChangeRequestEnabled?: boolean;
};

/**
 * Generic attribute group card that works for products, entities, and any future types
 *
 * No product/entity-specific logic - everything is configurable via props.
 */
export function AttributeGroupCard<T>({
  data,
  group,
  isChangeRequestEnabled = false,
  mutate,
  groupEndpoint,
  detailsComponent: DetailsComponent,
  onSaveSuccess,
  showMandatoryAsterisk = false,
  onCreateOption,
  onCreateImageAttribute,
  onCreateDocumentAttribute,
  product,
  updateApplicability,
  filterInvisibleAttributesInEditMode = true,
  isEditable = true,
  onAssociateDocumentAssets,
  objectSearchExcludeProfileId,
}: AttributeGroupCardConfig<T>): JSX.Element {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isChangeRequest, setIsChangeRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingObjectRefConfirmation, setPendingObjectRefConfirmation] =
    useState<ProductGroupFormValues | null>(null);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const { roleIsGuest, roleIsSomeKindOfBuyer, hasPermission } =
    useAuthContext();
  const methodsOfUseForm = useForm();
  const debugEnabled = isEditableAttributeDebugEnabled();
  const group_attributes_map = useMemo(() => {
    return group.attributes.reduce((acc, attr) => {
      acc[attr.id] = attr;
      return acc;
    }, {} as Record<string, AttributeSchema>);
  }, [group.attributes]);

  const allAttributes = useMemo(
    () => group.attributes as AttributeSchema[],
    [group.attributes]
  );

  const visibleAttributes = useMemo(
    () =>
      allAttributes.filter((attr) =>
        shouldShowObjectReferenceAttribute({
          inputType: attr.input_type,
          objectReferenceEnabled: object_reference_enabled,
        })
      ),
    [allAttributes, object_reference_enabled]
  );

  const editModeAttributes = useMemo(
    () =>
      filterInvisibleAttributesInEditMode
        ? visibleAttributes.filter((attr) => attr.is_visible !== false)
        : visibleAttributes,
    [visibleAttributes, filterInvisibleAttributesInEditMode]
  );

  useEffect(() => {
    if (!isEditMode || !debugEnabled) {
      return;
    }

    const hiddenByVisibility = allAttributes.filter(
      (attr) => attr.is_visible === false
    );

    console.info("[MDM Attribute Debug] Edit mode visibility filter", {
      group_id: group.id,
      group_name: group.name,
      total_attributes: allAttributes.length,
      edit_mode_attributes: editModeAttributes.length,
      filter_invisible_attributes_in_edit_mode:
        filterInvisibleAttributesInEditMode,
      filtered_out_attributes: hiddenByVisibility.map((attr) => ({
        id: attr.id,
        name: attr.name,
        display_name: attr.display_name,
        input_type: attr.input_type,
        is_visible: attr.is_visible,
        is_editable: attr.is_editable,
      })),
    });
  }, [
    allAttributes,
    debugEnabled,
    editModeAttributes.length,
    filterInvisibleAttributesInEditMode,
    group.id,
    group.name,
    isEditMode,
  ]);

  const hasObjectReferenceValueChanged = (
    formValues: ProductGroupFormValues
  ): boolean =>
    editModeAttributes.some((attribute) => {
      if (
        attribute.input_type !== "object_reference" ||
        attribute.is_not_applicable
      ) {
        return false;
      }
      return shouldConfirmObjectReferenceChange({
        existingValue: attribute.values,
        nextFormValue: formValues[attribute.id],
      });
    });

  const submitGroupChanges = async (formValues: ProductGroupFormValues) => {
    try {
      setIsLoading(true);
      const { values, values_to_delete } = buildAttributesPatchData(
        formValues,
        group_attributes_map
      );

      if (values.length === 0 && values_to_delete.length === 0) {
        notifySuccess(t("No changes to save"));
        setIsEditMode(false);
        return;
      }

      const { data: updatedData } = await axios.patch<T>(groupEndpoint, {
        values,
        values_to_delete,
      });

      if (onAssociateDocumentAssets) {
        const newDocumentAssetIds = values
          .filter(({ attribute_id }) =>
            isDocumentAttribute(group_attributes_map[attribute_id]?.input_type)
          )
          .map(({ value }) => (value as { asset_id?: string }).asset_id)
          .filter((id): id is string => Boolean(id));

        if (newDocumentAssetIds.length > 0) {
          await onAssociateDocumentAssets(newDocumentAssetIds);
        }
      }

      // Call optional success callback (for products to handle ID replacement)
      if (onSaveSuccess) {
        onSaveSuccess(updatedData);
      } else {
        await mutate();
      }

      notifySuccess(t("Your changes have been saved"));
      setIsEditMode(false);
    } catch (error) {
      const responseData = (error as AxiosError)?.response?.data;
      if (isAttributeEditRejected(responseData)) {
        notifyError(getAttributeEditRejectedMessage(responseData, t), {
          error,
        });
      } else {
        const errorMessage = (responseData as { message?: string })?.message;
        notifyError(
          errorMessage || t("There was an error saving your changes"),
          {
            error,
          }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (formValues: ProductGroupFormValues) => {
    if (hasObjectReferenceValueChanged(formValues)) {
      setPendingObjectRefConfirmation(formValues);
      return;
    }
    await submitGroupChanges(formValues);
  };

  const changeRequestAttributeOptions = useMemo(
    () =>
      resolveAttributeOptions(
        editModeAttributes.filter((attr) =>
          isAttributeVisibleInCard(attr, roleIsGuest, roleIsSomeKindOfBuyer)
        )
      ),
    [editModeAttributes, roleIsGuest, roleIsSomeKindOfBuyer]
  );

  const entity = data as {
    id: string;
    name: string;
    entity_type: EntityType;
    status: string;
  };

  const changeRequestContext = useMemo(
    () => ({
      variant: "group" as const,
      groupData: {
        groupName: group.display_name || group.name,
        groupId: group.id,
        entityName: entity.name,
        attributeOptions: changeRequestAttributeOptions,
      },
    }),
    [group, entity.name, changeRequestAttributeOptions]
  );

  const onSubmitChangeRequest = async (formValues: ChangeRequestFormValues) => {
    setIsSubmitting(true);
    try {
      await submitChangeRequest({
        tenant_id,
        entity,
        context: changeRequestContext,
        formValues,
        onSuccess: () => {
          setIsChangeRequest(false);
        },
      });
      notifySuccess(t("Change request submitted successfully."));
    } catch (error) {
      console.error(error);
      notifyError(t("Failed to submit change request."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateImageAttribute = (
    image_attr: ImageAttributeValue | null,
    attribute: AttributeSchema & { attribute_id?: string }
  ) => {
    methodsOfUseForm.setValue(attribute.id, image_attr);
    onCreateImageAttribute?.(image_attr, attribute);
  };
  const handleCreateDocumentAttribute = (
    docs: DocumentAttributeValue[],
    attribute: AttributeSchema & { attribute_id?: string }
  ) => {
    methodsOfUseForm.setValue(attribute.id, docs);
    onCreateDocumentAttribute?.(docs, attribute);
  };

  if (editModeAttributes.length === 0) {
    return <></>;
  }

  return (
    <AccordionCardWithEditFeatures
      title={group.display_name || group.name}
      handleEdit={isEditable ? () => setIsEditMode(true) : undefined}
      editing={isEditMode}
      expanded={true}
      editDisabled={group.attributes.every(
        (attr) => attr.is_editable === false
      )}
      handleChangeRequest={
        isChangeRequestEnabled &&
        entity.status !== "archived" &&
        !hasPermission(ENTITY_MODIFY_PERMISSIONS[entity.entity_type])
          ? () => setIsChangeRequest(true)
          : undefined
      }
      editDisabledReason={
        group.attributes.every((attr) => attr.is_editable === false)
          ? t(
              "Your role doesn't have edit permission for any attributes in this group."
            )
          : undefined
      }
    >
      {isChangeRequestEnabled && (
        <ChangeRequestFormModal
          open={isChangeRequest}
          onClose={() => setIsChangeRequest(false)}
          variant={changeRequestContext.variant}
          groupData={changeRequestContext.groupData}
          onSubmit={onSubmitChangeRequest}
          submitting={isSubmitting}
          assignee={product?.product_owner ?? null}
          assigneePermissions={[ENTITY_MODIFY_PERMISSIONS[entity.entity_type]]}
        />
      )}

      {!isEditMode && <DetailsComponent group={group} />}
      {isEditMode && (
        <EditFormWrapper>
          <AttributeGroupEditForm
            attributes={editModeAttributes}
            onSubmit={onSubmit}
            onCancel={() => setIsEditMode(false)}
            methodsOfUseForm={methodsOfUseForm}
            onCreateOption={onCreateOption || (async () => {})}
            onCreateImageAttribute={handleCreateImageAttribute}
            onCreateDocumentAttribute={handleCreateDocumentAttribute}
            product={product}
            isLoading={isLoading}
            updateApplicability={updateApplicability || (async () => {})}
            showApplicableButton={!!updateApplicability}
            showMandatoryAsterisk={showMandatoryAsterisk}
            has_reference_entity_mapping={group.has_reference_entity_mapping}
            group_id={group.id}
            objectSearchExcludeProfileId={
              objectSearchExcludeProfileId ??
              (data && typeof data === "object" && "id" in data
                ? String((data as { id: string }).id)
                : undefined)
            }
          />
        </EditFormWrapper>
      )}
      {pendingObjectRefConfirmation && (
        <ObjectReferenceChangeConfirmDialog
          show={true}
          onClose={() => setPendingObjectRefConfirmation(null)}
          onConfirm={async () => {
            const pendingValues = pendingObjectRefConfirmation;
            setPendingObjectRefConfirmation(null);
            if (pendingValues) {
              await submitGroupChanges(pendingValues);
            }
          }}
        />
      )}
    </AccordionCardWithEditFeatures>
  );
}
